import { LexoRank } from 'lexorank';

import { assertIsDefined } from '@gitbook/util-ts';

import { getEntropy } from './entropy';

export { disableOrderIndexEntropy, resetOrderIndexEntropy } from './entropy';

/**
 * Position at which to insert an item in an array, ordered by index.
 */
export interface OrderIndexPosition {
    /** orderIndex of the element before this position */
    before?: string;
    /** orderIndex of the element after this position */
    after?: string;
    /**
     * orderIndex of the element immediately after `after` in the current ordering.
     * Only needed when `before === after` (duplicate ranks) to spread the tie apart.
     */
    nextSiblingIndex?: string;
}

export interface OrderIndexAndFixup {
    /** New order index for the item being moved/inserted. */
    index: string;
    /**
     * If set, the `after` item's orderIndex should be updated to this value
     * before (or atomically with) applying `index`.
     */
    fixAfterIndex?: string;
}

/**
 * Like `createOrderIndex`, but also handles the case where `before === after`
 * by spreading the `after` item to a new position between itself and `nextSiblingIndex`,
 * then placing the moved item in between.
 *
 * For eg. let's consider A, B, C, D, X, Z where A and B have the same order. Before, if I move
 * C between A and B it would crash because it's impossible to put something between two identical
 * order. Now, what we do is set B's order to something between "before" so A and the next item after
 * so "C".
 * Then, the move can happen given B will be between A and C and differnt than A.
 *
 * Returns `fixAfterIndex` when the caller must update the `after` document.
 */
export function createOrderIndexAndFixup(at: OrderIndexPosition = {}): OrderIndexAndFixup {
    const before = at.before ? safeParse(at.before) : null;
    const after = at.after ? safeParse(at.after) : null;

    if (before && after && before.equals(after)) {
        // Spread `after` to a new rank between its current position and `nextSiblingIndex`,
        // then insert the moved item between `before` and the new `after` rank.
        const fixAfterIndex = createOrderIndex({ before: at.after, after: at.nextSiblingIndex });
        const index = createOrderIndex({ before: at.before, after: fixAfterIndex });
        return { index, fixAfterIndex };
    }

    return { index: createOrderIndex(at) };
}

export interface Orderable {
    orderIndex: string;
}

/**
 * Compare two orderable items.
 *  returns -1 when @a comes first
 *  return +1 when @b comes first
 */
export function compareOrderIndexes(a: Orderable, b: Orderable): number {
    const aOrderIndex = a.orderIndex;
    const bOrderIndex = b.orderIndex;

    if (aOrderIndex < bOrderIndex) {
        return -1;
    }
    if (aOrderIndex > bOrderIndex) {
        return 1;
    }
    return 0;
}

/**
 * Find the position to insert a new element of an arbitrary order index into a sorted array
 * of orderable elements, using binary search.
 *
 * **Note:** The input array must be pre-sorted for this function to work correctly.
 */
export function findInsertPositionInSortedOrderIndexes<T extends Orderable>(
    array: T[],
    insertValue: T
) {
    let left = 0;
    let right = array.length;
    let leftLast = left;
    let rightLast = right;

    while (left < right) {
        const inPos = Math.floor((right + left) / 2);
        const current = array[inPos];
        assertIsDefined(current);

        const compared = compareOrderIndexes(current, insertValue);
        if (compared < 0) {
            left = inPos;
        } else if (compared > 0) {
            right = inPos;
        } else {
            right = inPos;
            left = inPos;
        }

        if (leftLast === left && rightLast === right) {
            break;
        }
        leftLast = left;
        rightLast = right;
    }

    return right;
}

/**
 * Sort elements that are orderable.
 * It mutates the array.
 */
export function sortOrderIndex<T extends Orderable>(elements: T[]): T[] {
    return elements.sort(compareOrderIndexes);
}

/**
 * Get position to insert element at the end of a list.
 */
export function getOrderIndexEndPosition<T extends Orderable>(elements: T[]): OrderIndexPosition {
    if (elements.length === 0) {
        return {};
    }

    const indexes = elements.map((element) => element.orderIndex).sort();
    return { before: indexes[indexes.length - 1] };
}

/**
 * Creates a new index positionned alphabetically between before and after.
 *
 * Ordering of elements in a collection without storing a global list of keys in another document.
 * It uses a lexicographical rank, similar to what Atlassian uses in Jira.
 *
 * To handle ordering in a partial view (e.g when a user move an element in a collection while not viewing all elements),
 * we add noises to the end results by offsetting the rank by a random number (between 0 and ENTROPY).
 * It can still result in a collision, but it's unlikely.
 *
 * References documents:
 *  - https://stackoverflow.com/a/49956113
 *  - https://confluence.atlassian.com/jirakb/understand-the-lexorank-managment-page-in-jira-server-779159218.html
 *  - https://begriffs.com/posts/2018-03-20-user-defined-order.html
 *
 * @param at.before Index of the element just before
 * @param at.after Index of the element just after
 */
export function createOrderIndex(at: OrderIndexPosition = {}): string {
    const before = at.before ? safeParse(at.before) : null;
    const after = at.after ? safeParse(at.after) : null;

    let result: LexoRank;

    if (before && !after) {
        const noises = Math.floor(Math.random() * getEntropy());
        result = before.genNext();
        for (let index = 0; index < noises; index += 1) {
            result = result.genNext();
        }
    } else if (!before && after) {
        const noises = Math.floor(Math.random() * getEntropy());
        result = after.genPrev();
        for (let index = 0; index < noises; index += 1) {
            result = result.genPrev();
        }
    } else if (before && after) {
        const noises = Math.floor(Math.random() * getEntropy());
        result = before.between(after);
        for (let index = 0; index < noises; index += 1) {
            result = result.between(after);
        }
    } else {
        result = getRandomLexoRank();
    }

    return result.toString();
}

/** Stable ranks for replacing an entire sibling order, not concurrent individual insertions. */
export function createSequentialOrderIndexes(count: number): string[] {
    let rank = LexoRank.middle();
    return Array.from({ length: count }, () => {
        const index = rank.toString();
        rank = rank.genNext();
        return index;
    });
}

/**
 * Ranks for `count` new items placed together between `before` and `after`, in order. For laying
 * out a known set of items, not for concurrent insertions.
 *
 * @example
 * createOrderIndexesBetween(3, { before: '0|hzzzzz:', after: '0|i00007:' });
 * // ['0|i00001:', '0|i00003:', '0|i00005:']
 * createOrderIndexesBetween(3, { before: '0|hzzzzz:' });
 * // ['0|i00007:', '0|i0000f:', '0|i0000n:']
 *
 * // Chaining `createOrderIndex` instead lengthens every rank (with random noise):
 * // ['0|i00006:zzzzzzzz', '0|i00006:zzzzzzzzzzzzzzzzzzzzzzzzzr', …]
 */
export function createOrderIndexesBetween(
    count: number,
    at: Pick<OrderIndexPosition, 'before' | 'after'>
): string[] {
    const before = at.before ? safeParse(at.before) : null;
    const after = at.after ? safeParse(at.after) : null;
    const ranks: LexoRank[] = new Array(count);

    const fill = (start: number, end: number, low: LexoRank, high: LexoRank) => {
        if (start >= end) {
            return;
        }
        const middle = Math.floor((start + end) / 2);
        const rank = low.between(high);
        ranks[middle] = rank;
        fill(start, middle, low, rank);
        fill(middle + 1, end, rank, high);
    };

    if (before && after) {
        fill(0, count, before, after);
    } else if (before) {
        let rank = before;
        for (let index = 0; index < count; index += 1) {
            rank = rank.genNext();
            ranks[index] = rank;
        }
    } else if (after) {
        let rank = after;
        for (let index = count - 1; index >= 0; index -= 1) {
            rank = rank.genPrev();
            ranks[index] = rank;
        }
    } else {
        return createSequentialOrderIndexes(count);
    }

    return ranks.map((rank) => rank.toString());
}

/**
 * Safely parse a LexoRank.
 */
function safeParse(input: string): LexoRank {
    try {
        return LexoRank.parse(input);
    } catch (_err) {
        return getRandomLexoRank();
    }
}

/**
 * Get a default random LexoRank.
 */
export function getRandomLexoRank(): LexoRank {
    const noises = Math.floor(Math.random() * getEntropy());
    let result = LexoRank.middle();
    for (let index = 0; index < noises; index += 1) {
        result = result.genPrev();
    }
    return result;
}

export type { LexoRank };
